import { createId } from './raciConstants';

const assignmentKey = (activityId, columnId) => `${activityId}:${columnId}`;

export const createDemoRaciMatrix = () => {
    const colPm = createId();
    const colTech = createId();
    const colFinance = createId();
    const colVendor = createId();
    const colClient = createId();

    const act1 = createId();
    const act2 = createId();
    const act3 = createId();
    const act4 = createId();
    const act5 = createId();
    const act6 = createId();

    const columns = [
        { id: colPm, label: 'Project Manager', order: 0 },
        { id: colTech, label: 'Technology Lead', order: 1 },
        { id: colFinance, label: 'Finance', order: 2 },
        { id: colVendor, label: 'Vendor', order: 3 },
        { id: colClient, label: 'Client', order: 4 },
    ];

    const activities = [
        { id: act1, name: 'Validate integration budget', groupId: null, order: 0 },
        { id: act2, name: 'Define target-state architecture', groupId: null, order: 1 },
        { id: act3, name: 'Approve migration roadmap', groupId: null, order: 2 },
        { id: act4, name: 'Execute data migration', groupId: null, order: 3 },
        { id: act5, name: 'Complete cybersecurity assessment', groupId: null, order: 4 },
        { id: act6, name: 'Approve Day 1 readiness', groupId: null, order: 5 },
    ];

    const assignments = {
        [assignmentKey(act1, colPm)]: 'C',
        [assignmentKey(act1, colTech)]: 'C',
        [assignmentKey(act1, colFinance)]: 'R',
        [assignmentKey(act1, colVendor)]: 'I',
        [assignmentKey(act1, colClient)]: 'A',
        [assignmentKey(act2, colPm)]: 'C',
        [assignmentKey(act2, colTech)]: 'R',
        [assignmentKey(act2, colFinance)]: 'I',
        [assignmentKey(act2, colVendor)]: 'C',
        [assignmentKey(act2, colClient)]: 'A',
        [assignmentKey(act3, colPm)]: 'R',
        [assignmentKey(act3, colTech)]: 'C',
        [assignmentKey(act3, colFinance)]: 'I',
        [assignmentKey(act3, colVendor)]: 'C',
        [assignmentKey(act3, colClient)]: 'A',
        [assignmentKey(act4, colPm)]: 'A',
        [assignmentKey(act4, colTech)]: 'C',
        [assignmentKey(act4, colFinance)]: 'I',
        [assignmentKey(act4, colVendor)]: 'R',
        [assignmentKey(act4, colClient)]: 'I',
        [assignmentKey(act5, colPm)]: 'C',
        [assignmentKey(act5, colTech)]: 'R',
        [assignmentKey(act5, colFinance)]: 'I',
        [assignmentKey(act5, colVendor)]: 'C',
        [assignmentKey(act5, colClient)]: 'A',
        [assignmentKey(act6, colPm)]: 'R',
        [assignmentKey(act6, colTech)]: 'C',
        [assignmentKey(act6, colFinance)]: 'C',
        [assignmentKey(act6, colVendor)]: 'I',
        [assignmentKey(act6, colClient)]: 'A',
    };

    return {
        version: 1,
        groups: [],
        columns,
        activities,
        assignments,
    };
};

export const createEmptyRaciMatrix = () => ({
    version: 1,
    groups: [],
    columns: [],
    activities: [],
    assignments: {},
});

export const isRaciMatrixEmpty = matrix =>
    matrix.activities.length === 0 && matrix.columns.length === 0 && matrix.groups.length === 0;

export const sortByOrder = items => [...items].sort((a, b) => a.order - b.order);

export const getSortedColumns = matrix => sortByOrder(matrix.columns);

export const getSortedGroups = matrix => sortByOrder(matrix.groups);

export const getActivitiesForGroup = (matrix, groupId) =>
    sortByOrder(matrix.activities.filter(a => a.groupId === groupId));

export const getUngroupedActivities = matrix => sortByOrder(matrix.activities.filter(a => !a.groupId));

export const getAssignment = (matrix, activityId, columnId) => matrix.assignments[assignmentKey(activityId, columnId)] || '';

export const activityHasAssignments = (matrix, activityId) =>
    matrix.columns.some(col => Boolean(matrix.assignments[assignmentKey(activityId, col.id)]));

export const columnHasAssignments = (matrix, columnId) =>
    matrix.activities.some(act => Boolean(matrix.assignments[assignmentKey(act.id, columnId)]));

export const isActivityDraft = activity => !activity.name?.trim();

/** Activities with blank names exist only in the UI until the user names them. */
export const prepareRaciMatrixForPersist = matrix => {
    const activities = matrix.activities.filter(activity => !isActivityDraft(activity));
    const persistedIds = new Set(activities.map(activity => activity.id));
    const assignments = Object.fromEntries(
        Object.entries(matrix.assignments).filter(([key]) => persistedIds.has(key.split(':')[0])),
    );
    return { ...matrix, activities, assignments };
};

export const getAccountableWarnings = matrix => {
    const warnings = [];
    if (matrix.columns.length === 0) {
        return warnings;
    }
    matrix.activities.forEach(activity => {
        if (isActivityDraft(activity)) {
            return;
        }
        if (!activityHasAssignments(matrix, activity.id)) {
            return;
        }
        const accountableCount = matrix.columns.reduce((count, col) => {
            const value = getAssignment(matrix, activity.id, col.id);
            return value === 'A' ? count + 1 : count;
        }, 0);
        const label = activity.name.trim();
        if (accountableCount === 0) {
            warnings.push(`"${label}" has no Accountable role.`);
        } else if (accountableCount > 1) {
            warnings.push(`"${label}" has multiple Accountable roles.`);
        }
    });
    return warnings;
};

const reindexOrders = items => items.map((item, index) => ({ ...item, order: index }));

export const setAssignment = (matrix, activityId, columnId, value) => {
    const key = assignmentKey(activityId, columnId);
    const assignments = { ...matrix.assignments };
    if (!value) {
        delete assignments[key];
    } else {
        assignments[key] = value;
    }
    return { ...matrix, assignments };
};

export const addActivity = (matrix, { name = '', groupId = null } = {}) => {
    const siblings = matrix.activities.filter(a => a.groupId === groupId);
    const order = siblings.length;
    const activity = { id: createId(), name, groupId, order };
    return {
        matrix: { ...matrix, activities: [...matrix.activities, activity] },
        activityId: activity.id,
    };
};

export const updateActivityName = (matrix, activityId, name) => ({
    ...matrix,
    activities: matrix.activities.map(a => (a.id === activityId ? { ...a, name } : a)),
});

export const deleteActivity = (matrix, activityId) => {
    const groupId = matrix.activities.find(a => a.id === activityId)?.groupId ?? null;
    const remaining = matrix.activities.filter(a => a.id !== activityId);
    const siblings = sortByOrder(remaining.filter(a => a.groupId === groupId));
    const reindexed = remaining.map(a => {
        if (a.groupId !== groupId) return a;
        const idx = siblings.findIndex(s => s.id === a.id);
        return { ...a, order: idx };
    });
    const assignments = { ...matrix.assignments };
    Object.keys(assignments).forEach(key => {
        if (key.startsWith(`${activityId}:`)) delete assignments[key];
    });
    return { ...matrix, activities: reindexed, assignments };
};

export const moveActivityWithinGroup = (matrix, activityId, direction) => {
    const activity = matrix.activities.find(a => a.id === activityId);
    if (!activity) return matrix;
    const siblings = sortByOrder(matrix.activities.filter(a => a.groupId === activity.groupId));
    const index = siblings.findIndex(a => a.id === activityId);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= siblings.length) return matrix;
    const reordered = [...siblings];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, removed);
    const orderMap = new Map(reordered.map((a, i) => [a.id, i]));
    return {
        ...matrix,
        activities: matrix.activities.map(a => {
            if (a.groupId !== activity.groupId) return a;
            return { ...a, order: orderMap.get(a.id) ?? a.order };
        }),
    };
};

export const moveActivityToGroup = (matrix, activityId, groupId) => {
    const activity = matrix.activities.find(a => a.id === activityId);
    if (!activity) return matrix;
    const oldGroupId = activity.groupId;
    const targetSiblings = matrix.activities.filter(a => a.groupId === groupId && a.id !== activityId);
    const newOrder = targetSiblings.length;
    let next = {
        ...matrix,
        activities: matrix.activities.map(a => (a.id === activityId ? { ...a, groupId, order: newOrder } : a)),
    };
    const reindexGroup = gid => {
        const siblings = sortByOrder(next.activities.filter(a => a.groupId === gid));
        const orderMap = new Map(siblings.map((a, i) => [a.id, i]));
        next = {
            ...next,
            activities: next.activities.map(a => {
                if (a.groupId !== gid) return a;
                return { ...a, order: orderMap.get(a.id) ?? a.order };
            }),
        };
    };
    reindexGroup(oldGroupId);
    if (groupId !== oldGroupId) reindexGroup(groupId);
    return next;
};

export const addColumn = (matrix, label) => {
    const order = matrix.columns.length;
    const column = { id: createId(), label, order };
    return { ...matrix, columns: [...matrix.columns, column] };
};

export const updateColumnLabel = (matrix, columnId, label) => ({
    ...matrix,
    columns: matrix.columns.map(c => (c.id === columnId ? { ...c, label } : c)),
});

export const deleteColumn = (matrix, columnId) => {
    const remaining = matrix.columns.filter(c => c.id !== columnId);
    const assignments = { ...matrix.assignments };
    Object.keys(assignments).forEach(key => {
        if (key.endsWith(`:${columnId}`)) delete assignments[key];
    });
    return { ...matrix, columns: reindexOrders(remaining), assignments };
};

export const moveColumn = (matrix, columnId, direction) => {
    const columns = sortByOrder(matrix.columns);
    const index = columns.findIndex(c => c.id === columnId);
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= columns.length) return matrix;
    const reordered = [...columns];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, removed);
    return { ...matrix, columns: reindexOrders(reordered) };
};

export const addGroup = (matrix, name) => {
    const order = matrix.groups.length;
    const group = { id: createId(), name, order };
    return { ...matrix, groups: [...matrix.groups, group] };
};

export const updateGroupName = (matrix, groupId, name) => ({
    ...matrix,
    groups: matrix.groups.map(g => (g.id === groupId ? { ...g, name } : g)),
});

export const deleteGroup = (matrix, groupId) => {
    const remainingGroups = matrix.groups.filter(g => g.id !== groupId);
    const activities = matrix.activities.map(a => (a.groupId === groupId ? { ...a, groupId: null } : a));
    const ungrouped = sortByOrder(activities.filter(a => !a.groupId));
    const orderMap = new Map(ungrouped.map((a, i) => [a.id, i]));
    return {
        ...matrix,
        groups: reindexOrders(remainingGroups),
        activities: activities.map(a => {
            if (a.groupId) return a;
            return { ...a, order: orderMap.get(a.id) ?? a.order };
        }),
    };
};

export const moveGroup = (matrix, groupId, direction) => {
    const groups = sortByOrder(matrix.groups);
    const index = groups.findIndex(g => g.id === groupId);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= groups.length) return matrix;
    const reordered = [...groups];
    const [removed] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, removed);
    return { ...matrix, groups: reindexOrders(reordered) };
};

